import { useCallback, useEffect, useRef, useState } from 'react'
import { api, isNotFound, methodFile, unavailableOf, type Unavailable } from '../v4/api'
import type { Conversation, Period, Plan, Progress, RunState } from '../v4/contracts'
import { followRun, type Followed } from '../v4/follow'
import { isStillLoading } from './unavailableText'

/**
 * The old builder's controller over one v4 run: the run as the API shows it, its progress, and the
 * actions the engine offers (start, approve, answer an ask, send a method file, cancel, rerun).
 * Following a run is `followRun`, the same loop the v4 page uses, so both surfaces see the same run
 * the same way. Errors are the API's: a 409/503 refusal becomes `unavailable` (why workflows cannot
 * run for this organization right now), anything else the `error` line.
 */
export interface WorkflowRun {
  runId: string | null
  run: RunState | null
  progress: Progress | null
  /** When `progress` was received (epoch ms), so a running clock can count on from it. */
  progressAt: number
  /** A followed run whose first state has not landed yet. */
  loading: boolean
  /** An action is in flight. */
  busy: boolean
  /** The approval is on its way: every Run button says "Starting…" until it comes back. */
  approving: boolean
  error: string
  /**
   * A request outlived the browser's deadline; the server goes on with it. `running`: the request
   * was about a run this page follows (planning after an answer or a file takes minutes), so the
   * run's own updates clear it. `not_started`: a start or rerun that never handed back a run id, so
   * there is nothing to follow and the engineer has to look again or ask again.
   */
  slow: 'running' | 'not_started' | null
  unavailable: Unavailable | null
  /** The API has no such run for this organization (404): gone, or another organization's. */
  notFound: boolean
  /** Starts a run from a question, as a follow-up when a conversation is given; the new run's id, or null when the API refused. */
  start(question: string, conversation?: Conversation): Promise<string | null>
  /** Approves the run's current plan version (the first node's Run pill). */
  approve(): Promise<void>
  cancel(): Promise<void>
  answer(resumeToken: string, values: Record<string, string>): Promise<void>
  sendFile(file: File): Promise<void>
  /** The approved plan on a new period, as a new run; its id, or null when refused. */
  rerun(period: Period): Promise<string | null>
  /** The plan awaiting approval, edited: true once the engine took it as a new version; false when it refused (the reason is the error line). */
  editPlan(plan: Plan): Promise<boolean>
  clearError(): void
}

/** How long a file refused because the organization's files are still being read waits before it is sent again. */
export const FILE_RESEND_MS = 15_000
/**
 * Sends of such a file before the page says it was not sent: about 3 minutes, past the 1 to 2 a
 * rebuild takes once a new document joins the organization's files (run-94a8ee6c lost a file to one).
 */
export const FILE_SENDS = 12

export function useWorkflowRun(runId: string | null): WorkflowRun {
  const [run, setRun] = useState<RunState | null>(null)
  const [followed, setFollowed] = useState<Followed | null>(null)
  const [error, setError] = useState('')
  const [slow, setSlow] = useState<WorkflowRun['slow']>(null)
  const [unavailable, setUnavailable] = useState<Unavailable | null>(null)
  const [notFound, setNotFound] = useState(false)
  const [busy, setBusy] = useState(false)
  // The run whose approval is on its way: "Starting…" belongs to that run only, not to one opened meanwhile.
  const [approvingRun, setApprovingRun] = useState<string | null>(null)
  // The error line came from following the run (a dropped poll, "Failed to fetch"), not from an
  // action: the run's next answer clears it, while an action's refusal stays until the next action.
  const pollError = useRef(false)
  // Aborted when the page leaves the run it was following, so a file still waiting to be sent again
  // stops instead of holding the next run busy or putting its error there.
  const leftRun = useRef(new AbortController())

  // True when the failure went to the error line.
  const fail = useCallback((e: Error, startsRun = false) => {
    const u = unavailableOf(e)
    if (isNotFound(e)) setNotFound(true)
    else if ((e as Error & { isTimeout?: boolean }).isTimeout) setSlow(startsRun ? 'not_started' : 'running')
    else if (u) setUnavailable(u)
    else {
      pollError.current = false
      setError(e.message)
      return true
    }
    return false
  }, [])

  // followRun keeps a record of runs by id (the v4 page follows a group); this hook follows one.
  useEffect(() => {
    const left = new AbortController()
    leftRun.current = left
    setRun(null)
    setFollowed(null)
    setError('')
    setSlow(null)
    setUnavailable(null)
    setNotFound(false)
    setBusy(false)
    if (!runId) return () => left.abort()
    // The run answered again (its state, or the progress polled every 2 s): a "still loading" or
    // "taking longer than usual" notice is over, so it does not stay on a run that is moving.
    const answered = () => {
      setSlow(null)
      setUnavailable((u) => (u && isStillLoading(u) ? null : u))
      if (pollError.current) {
        pollError.current = false
        setError('')
      }
    }
    const followFailed = (e: Error) => {
      if (fail(e)) pollError.current = true
    }
    const stop = followRun(
      runId,
      (f) => {
        answered()
        setRun((prev) => f(prev ? { [runId]: prev } : {})[runId] ?? null)
      },
      (f) => {
        answered()
        setFollowed((prev) => f(prev ? { [runId]: prev } : {})[runId] ?? null)
      },
      followFailed,
      // This page reads only what each call did (runStory.stepFailure); the whole calls, every
      // prompt and tool row, made an admin's load take seconds (FS-724).
      'summary',
    )
    return () => {
      left.abort()
      stop()
    }
  }, [runId, fail])

  const act = useCallback(
    async <T,>(fn: () => Promise<T>, startsRun = false): Promise<T | null> => {
      setError('')
      setSlow(null)
      setUnavailable(null)
      setBusy(true)
      try {
        return await fn()
      } catch (e) {
        fail(e as Error, startsRun)
        return null
      } finally {
        setBusy(false)
      }
    },
    [fail],
  )

  const start = useCallback(
    (question: string, conversation?: Conversation) =>
      act(() => (conversation ? api.start(question, conversation) : api.start(question)), true).then((r) => r?.runIds[0] ?? null),
    [act],
  )
  const approve = useCallback(async () => {
    if (!run) return
    const id = run.runId
    setApprovingRun(id)
    try {
      await act(() => api.approve(id, run.planVersion))
    } finally {
      // A newer approval of another run keeps its own flag.
      setApprovingRun((cur) => (cur === id ? null : cur))
    }
  }, [act, run])
  const cancel = useCallback(async () => {
    if (!runId) return
    await act(() => api.cancel(runId))
  }, [act, runId])
  const answer = useCallback(
    async (resumeToken: string, values: Record<string, string>) => {
      await act(() => api.answer(resumeToken, values))
    },
    [act],
  )
  const sendFile = useCallback(
    async (file: File) => {
      if (!runId) return
      const left = leftRun.current.signal
      await act(async () => {
        const body = await methodFile(file)
        for (let sends = 1; ; sends++) {
          try {
            return await api.sendFile(runId, body)
          } catch (e) {
            // The page has moved to another run: what this send ends with is not that run's.
            if (left.aborted) return null
            const u = unavailableOf(e)
            if (!u || !isStillLoading(u)) throw e
            // The run never got it: say so, rather than a "still loading" hint the next poll clears.
            if (sends >= FILE_SENDS)
              throw new Error("Your file was not sent: this organization's files are still being read. Send it again in a minute.")
            setUnavailable(u)
            await new Promise<void>((resolve) => {
              const timer = setTimeout(resolve, FILE_RESEND_MS)
              left.addEventListener('abort', () => (clearTimeout(timer), resolve()), { once: true })
            })
            if (left.aborted) return null
          }
        }
      })
    },
    [act, runId],
  )
  const rerun = useCallback(
    (period: Period) =>
      runId ? act(() => api.rerun(runId, period), true).then((r) => r?.runIds[0] ?? null) : Promise.resolve(null),
    [act, runId],
  )
  const editPlan = useCallback(
    async (plan: Plan) => (runId ? (await act(() => api.edit(runId, plan))) !== null : false),
    [act, runId],
  )
  const clearError = useCallback(() => {
    setError('')
    setSlow(null)
    setUnavailable(null)
  }, [])

  return {
    runId,
    run,
    progress: followed?.p ?? null,
    progressAt: followed?.at ?? 0,
    loading: runId !== null && run === null && !error && !unavailable && !notFound,
    busy,
    approving: approvingRun !== null && approvingRun === runId,
    error,
    slow,
    unavailable,
    notFound,
    start,
    approve,
    cancel,
    answer,
    sendFile,
    rerun,
    editPlan,
    clearError,
  }
}
